/**
 * =============================================================================
 * Hook: useSetDefaultAffiliateProgram
 * Versão: 1.0.0
 *
 * Descrição:
 *   Mutation que marca um programa de indicação como o "padrão" do tenant.
 *   Esse padrão é usado pela rota genérica `/afiliado/<slug>/cadastro` para
 *   resolver automaticamente para qual programa o novo afiliado deve se
 *   inscrever quando o link público do tenant é compartilhado.
 *
 *   Reusa a RPC segura `set_default_affiliate_program` (tenant validado +
 *   log de bypass + RAISE EXCEPTION).
 * =============================================================================
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const log = createLogger('useSetDefaultAffiliateProgram');

export function useSetDefaultAffiliateProgram() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (programId: string) => {
      if (!programId) throw new Error('programId é obrigatório');

      log.debug('Definindo programa padrão', { programId });

      const { data, error } = await (supabase.rpc as any)(
        'set_default_affiliate_program',
        { p_program_id: programId },
      );

      if (error) {
        log.error('Erro ao definir programa padrão', error);
        throw new Error(error.message ?? 'Falha ao definir programa padrão.');
      }

      return data as string;
    },
    onSuccess: () => {
      // Invalida a listagem para refletir o novo padrão
      qc.invalidateQueries({ queryKey: ['referral-programs'] });
      qc.invalidateQueries({ queryKey: ['default-affiliate-program'] });
      toast.success('Programa definido como padrão!');
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
